/*
 * Capture form builder — full field-level editor for one form. Loads
 * (or creates) a form, lets the user add/edit/reorder/delete fields,
 * persists each save through the existing /api/capture/forms endpoint.
 *
 * Field types covered for the MVP:
 *   - text         (single-line)
 *   - textarea     (multi-line free text)
 *   - number       (numeric with optional min / max)
 *   - date         (YYYY-MM-DD picker)
 *   - select       (dropdown with inline option list)
 *   - data-element (binds the answer to a DHIS2 dataElementId)
 *
 * Each field can be required and can carry a free-form `validation`
 * string the runtime will interpret (regex for text, range expression
 * for number, etc.) — the runtime evaluator ships with capture
 * submission processing, not with this builder.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, ApiError } from "../lib/api.js";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";

type FieldType = "text" | "textarea" | "number" | "date" | "select" | "data-element";

interface FormField {
  id: string;
  label: string;
  type: FieldType;
  required: boolean;
  /** Plain free-form validation hint for the future evaluator. */
  validation?: string;
  /** select-only: list of {value,label} options. */
  options?: Array<{ value: string; label: string }>;
  /** data-element-only: DHIS2 dataElementId this field binds to. */
  dataElementId?: string;
  /** Inline help text shown under the field label. */
  description?: string;
}

interface FormShape {
  id?: string;
  name: string;
  description: string;
  domain: "aggregate" | "tracker" | "individual";
  status: "draft" | "published" | "archived";
  fields: FormField[];
}

const DEFAULT_FORM: FormShape = {
  name: "",
  description: "",
  domain: "aggregate",
  status: "draft",
  fields: []
};

function newField(type: FieldType): FormField {
  const id = `f-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  return {
    id,
    label: type === "data-element" ? "DHIS2 data element" : "Untitled field",
    type,
    required: false,
    ...(type === "select" ? { options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }] } : {})
  };
}

export default function CaptureFormBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id");

  const [form, setForm] = useState<FormShape>(DEFAULT_FORM);
  const [loading, setLoading] = useState<boolean>(!!editId);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editId) return;
    api.listCaptureForms()
      .then((r) => {
        const found = r.forms.find((f) => f.id === editId);
        if (!found) {
          setError(`Form ${editId} not found.`);
          return;
        }
        setForm({
          id: found.id,
          name: found.name,
          description: found.description ?? "",
          domain: found.domain as FormShape["domain"],
          status: found.status as FormShape["status"],
          fields: (found.fields as FormField[]) ?? []
        });
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [editId]);

  function updateField(idx: number, patch: Partial<FormField>) {
    setForm((f) => ({ ...f, fields: f.fields.map((x, i) => (i === idx ? { ...x, ...patch } : x)) }));
  }
  function moveField(idx: number, dir: -1 | 1) {
    setForm((f) => {
      const target = idx + dir;
      if (target < 0 || target >= f.fields.length) return f;
      const next = f.fields.slice();
      const [moved] = next.splice(idx, 1);
      next.splice(target, 0, moved);
      return { ...f, fields: next };
    });
  }
  function deleteField(idx: number) {
    setForm((f) => ({ ...f, fields: f.fields.filter((_, i) => i !== idx) }));
  }
  function addField(type: FieldType) {
    setForm((f) => ({ ...f, fields: [...f.fields, newField(type)] }));
  }

  async function save() {
    if (!form.name.trim()) { setError("Name is required."); return; }
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await api.upsertCaptureForm({
        id: form.id,
        name: form.name,
        description: form.description,
        status: form.status,
        domain: form.domain,
        fields: form.fields
      });
      setNotice(`Saved (${r.id}).`);
      if (!form.id) {
        // First save — bounce so reloads land in edit mode.
        navigate(`/app/capture/forms/new?id=${encodeURIComponent(r.id)}`, { replace: true });
        setForm((f) => ({ ...f, id: r.id }));
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  }

  async function publish() {
    setForm((f) => ({ ...f, status: "published" }));
    setTimeout(() => void save(), 0);
  }

  const fieldTypes: Array<{ key: FieldType; label: string }> = useMemo(() => [
    { key: "text",         label: "Text" },
    { key: "textarea",     label: "Long text" },
    { key: "number",       label: "Number" },
    { key: "date",         label: "Date" },
    { key: "select",       label: "Dropdown" },
    { key: "data-element", label: "DHIS2 data element" }
  ], []);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>{editId ? "Edit form" : "New form"}</h1>
          <p className="muted">Drag-free field editor — use the arrow buttons to reorder. Save updates persist immediately; publish to expose the form to submitters.</p>
        </div>
        <div className="actions">
          <Link to="/app/capture/forms" className="btn btn-secondary">Back to forms</Link>
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save"}
          </button>
          {form.id && (
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void publish()}>
              {form.status === "published" ? "Re-publish" : "Publish"}
            </button>
          )}
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}
      {notice && <div className="ts-alert ts-alert-info">{notice}</div>}
      {loading && <div className="ts-alert ts-alert-info">Loading form…</div>}

      <section className="empty-state-card" style={{ marginBottom: 16 }}>
        <h2 style={{ marginTop: 0 }}>Form details</h2>
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" }}>
          <label className="transfer-filter">
            Name
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label className="transfer-filter">
            Domain
            <select value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value as FormShape["domain"] })}>
              <option value="aggregate">Aggregate</option>
              <option value="tracker">Tracker</option>
              <option value="individual">Individual-level</option>
            </select>
          </label>
          <label className="transfer-filter" style={{ gridColumn: "1 / -1" }}>
            Description
            <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </label>
          <label className="transfer-filter">
            Status
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as FormShape["status"] })}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </label>
        </div>
      </section>

      <section className="empty-state-card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0 }}>Fields ({form.fields.length})</h2>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {fieldTypes.map((t) => (
              <button key={t.key} type="button" className="btn btn-ghost btn-sm" onClick={() => addField(t.key)}>
                + {t.label}
              </button>
            ))}
          </div>
        </div>
        {form.fields.length === 0 && (
          <p className="muted" style={{ marginTop: 12 }}>
            No fields yet. Add fields with the buttons above. Each field becomes a question on the rendered form.
          </p>
        )}
        <ol className="capture-fields">
          {form.fields.map((field, idx) => (
            <li key={field.id} className="capture-field">
              <div className="capture-field-head">
                <span className="capture-field-num">{idx + 1}</span>
                <span className="pill pill-muted">{field.type}</span>
                <div style={{ flex: 1 }} />
                <button type="button" className="btn btn-ghost btn-sm" disabled={idx === 0} onClick={() => moveField(idx, -1)} title="Move up">↑</button>
                <button type="button" className="btn btn-ghost btn-sm" disabled={idx === form.fields.length - 1} onClick={() => moveField(idx, 1)} title="Move down">↓</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => deleteField(idx)} title="Delete">✕</button>
              </div>
              <div className="capture-field-body">
                <label className="transfer-filter">
                  Label
                  <input type="text" value={field.label} onChange={(e) => updateField(idx, { label: e.target.value })} />
                </label>
                <label className="transfer-filter">
                  <span><input type="checkbox" checked={field.required} onChange={(e) => updateField(idx, { required: e.target.checked })} /> Required</span>
                </label>
                <label className="transfer-filter" style={{ gridColumn: "1 / -1" }}>
                  Description / help text
                  <input type="text" value={field.description ?? ""} onChange={(e) => updateField(idx, { description: e.target.value })} />
                </label>
                {field.type !== "select" && (
                  <label className="transfer-filter" style={{ gridColumn: "1 / -1" }}>
                    Validation hint (regex / range / freeform — evaluator processes at submission time)
                    <input type="text" value={field.validation ?? ""} onChange={(e) => updateField(idx, { validation: e.target.value })} placeholder={field.type === "number" ? "e.g. min:0,max:100" : "e.g. ^[A-Z]{3}-\\d+$"} />
                  </label>
                )}
                {field.type === "select" && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>Options</div>
                    <SelectOptionsEditor
                      options={field.options ?? []}
                      onChange={(opts) => updateField(idx, { options: opts })}
                    />
                  </div>
                )}
                {field.type === "data-element" && (
                  <label className="transfer-filter" style={{ gridColumn: "1 / -1" }}>
                    DHIS2 data element ID
                    <input type="text" value={field.dataElementId ?? ""} onChange={(e) => updateField(idx, { dataElementId: e.target.value })} placeholder="e.g. de-doses" />
                  </label>
                )}
              </div>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function SelectOptionsEditor({ options, onChange }: { options: Array<{ value: string; label: string }>; onChange: (next: Array<{ value: string; label: string }>) => void }) {
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {options.map((opt, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 6 }}>
          <input type="text" value={opt.value} onChange={(e) => onChange(options.map((o, j) => j === i ? { ...o, value: e.target.value } : o))} placeholder="value" />
          <input type="text" value={opt.label} onChange={(e) => onChange(options.map((o, j) => j === i ? { ...o, label: e.target.value } : o))} placeholder="label" />
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(options.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange([...options, { value: "", label: "" }])}>+ Add option</button>
    </div>
  );
}
